import React, { useEffect, useState, useCallback, useRef } from "react";
import {  Box,  Flex,  Input,  Text,  Badge,  Spinner,  IconButton,  Tooltip,  Modal,  ModalOverlay,  ModalContent,  ModalHeader,  ModalFooter,  ModalBody,  ModalCloseButton,  Button,  Table,  Thead,  Tbody,  Tr,  Th,  Td,
  TableContainer,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { CustomTable, CustomToast } from "../../../components";
import { Column } from "../../../components/customTable/CustomTable";
import { baseUrl, useFetch } from "../../../hooks/useFetch";
import { showDateTime } from "../../../utilities/helper";
import { DownloadIcon } from "@chakra-ui/icons";
import useDebounce from "../../../hooks/useDebounce";
// import config from "../../../../"; // adjust path if needed

interface BulkUploadProcessItem {
  id?: number | string;
  bulkUploadProcessId?: number | string;
  BulkUploadProcessId?: number | string;
  uploadedBy?: string;
  userName?: string;
  UserName?: string;
  createdBy?: string;
  fileName?: string;
  FileName?: string;
  name?: string;
  uploadedTime?: string;
  UploadedTime?: string;
  createdOn?: string;
  CreatedOn?: string;
  uploadTime?: string;
  isProcess?: number | boolean | string;
  IsProcess?: number | boolean | string;
  completedTime?: string | null;
  CompletedTime?: string | null;
  completedDate?: string | null;
  processTime?: string | null;
  updatedOn?: string | null;
  isProcessTime?: string | null;
  failedCount?: number;
  FailedCount?: number;
  failedRows?: number;
  FailedRows?: number;
  failedRecords?: number;
  failed?: number;
}

interface FailedRowItem {
  contact?: string;
  Contact?: string;
  email?: string;
  Email?: string;
  phone?: string;
  Phone?: string;
  reason?: string;
  Reason?: string;
}

const BulkUploadLead: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<BulkUploadProcessItem[]>([]);
  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [pagination, setPagination] = useState({ skip: 0, limit: 10 });
  const debounceSearch = useDebounce(search, 700);

  // Modal State for Failed Rows
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<BulkUploadProcessItem | null>(null);
  const [failedRows, setFailedRows] = useState<FailedRowItem[]>([]);
  const [loadingFailedRows, setLoadingFailedRows] = useState(false);
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const isFetchingRef = useRef(false);

  const loadBulkUploadHistory = useCallback(async (isSilent = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    if (!isSilent) setLoading(true);
    try {
      const queryParams = `skip=${pagination.skip}&limit=${pagination.limit}&search=${encodeURIComponent(debounceSearch)}`;
      let res = await fetchApi("Status/LeadgetBulkUploadProcess", "GET", null, queryParams, "");
      if (res) {
        const list = Array.isArray(res)
          ? res
          : res.bulkUploads || res.data || res.list || res.result || [];
        setTableData(list);
        
        const total = typeof res === "object" && res !== null && "totalCount" in res ? res.totalCount : list.length;
        setTotalRecords(total);
      }
    } catch (err) {
      console.error("Failed to load bulk upload history:", err);
    } finally {
      if (!isSilent) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchApi, pagination.skip, pagination.limit, debounceSearch]);

  const loadRef = useRef(loadBulkUploadHistory);
  useEffect(() => {
    loadRef.current = loadBulkUploadHistory;
  }, [loadBulkUploadHistory]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadRef.current(true);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadRef.current();
  }, [pagination.skip, pagination.limit, debounceSearch, loadRef]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      skip: 0,
    }));
  }, [debounceSearch]);

  function pageChangeFunction(e: { skip: number; limit: number }) {
    setPagination(e);
  }

  const handleViewFailed = async (row: BulkUploadProcessItem) => {
    setSelectedProcess(row);
    setIsFailedModalOpen(true);
    setLoadingFailedRows(true);
    setFailedRows([]);
    const processId = row.id ?? row.BulkUploadProcessId;
    if (processId !== undefined && processId !== null) {
      try {
        let res = await fetchApi(`Status/GetBulkUploadFailedRows/${processId}`, "GET", null, null, "");    
        if (res) {
          const list = Array.isArray(res) ? res : res.data || res.list || res.rows || [];
          setFailedRows(list);
        }
      } catch (err) {
        console.error("Error loading failed rows:", err);
      }
    }
    setLoadingFailedRows(false);
  };





const handleDownloadFailedRows = async (row: BulkUploadProcessItem) => {
  const processId = row.id ?? row.BulkUploadProcessId;
  const fileName = row.fileName || row.FileName || "FailedLeadRows";

  if (!processId) return;

  try {
    const response = await fetch(
      `${baseUrl}Status/DownloadFailedRows/${processId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // if your API is authorized
        },
      }
    );

    if (!response.ok) {
      throw new Error("Download failed");
    }

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `FailedLeadRows_${fileName}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error(err);
    addToast({
      message: "Download Failed",
      status: "error",
    });
  }
};




  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text className="font-poppins text_xxl text_semibold" fontSize="xl" fontWeight="bold">
            Bulk Upload
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Track and monitor the status of all bulk lead import processes
          </Text>
        </Box>

        <Flex gap={3} align="center">
          <Input
            placeholder="Search by file or user..."
            width="280px"
            bg="white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            borderRadius="md"
          />

          <Tooltip label="Refresh Status">
            <IconButton
              aria-label="Refresh"
              icon={<RepeatIcon />}
              onClick={() => loadBulkUploadHistory()}
              isLoading={loading}
              bg="white"
              border="1px solid #E2E8F0"
              _hover={{ bg: "gray.100" }}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <CustomTable
        value={tableData}
        onPageChange={pageChangeFunction}
        rowsPerPage={pagination.limit}
        totalRecords={totalRecords}
        headerBg="#E6F0FF"
        headerTextColor="#1A202C"
        loading={loading}
        emptyMessage="No bulk upload records found"
      >
        <Column header="S.No" body={(_row: any, index: number) => pagination.skip + index + 1} />

        <Column
          header="File Name"
          body={(row: BulkUploadProcessItem) => {
            const fileName = row.fileName || row.FileName || row.name || "-";
            return (
              <Text color="blue.600" fontWeight="500">
                {fileName}
              </Text>
            );
          }}
        />

        <Column
          header="Uploaded By"
          body={(row: BulkUploadProcessItem) => {
            const uploadedBy =
              row.uploadedBy ||
             
              "-";
            return <Text fontWeight="500">{uploadedBy}</Text>;
          }}
        />

        <Column
          header="Uploaded Time"
          body={(row: BulkUploadProcessItem) => {
            const uploadedTime =
              row.uploadedTime ||
              row.UploadedTime ||
              row.createdOn ||
              row.CreatedOn ||
              row.uploadTime;
            return <Text>{showDateTime(uploadedTime)}</Text>;
          }}
        />

        <Column
          header="Status"
          body={(row: BulkUploadProcessItem) => {
            const isProc = row.isProcess ?? row.IsProcess;
            const isCompleted =
              isProc === 1 || isProc === true || isProc === "1";
            const isFailed =
              isProc === 2 || isProc === "2";

            if (isCompleted) {
              return (
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="#E6F4EA"
                  color="#137333"
                  border="1px solid #CEEAD6"
                  fontSize="0.85rem"
                  fontWeight="600"
                  textTransform="none"
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <Box as="span" fontSize="0.75rem">🟢</Box>
                  Completed
                </Badge>
              );
            }

            if (isFailed) {
              return (
                <Badge px={3} py={1} borderRadius="full" bg="#FCE8E6" color="#C5221F" border="1px solid #FAD2CF" 
                  fontSize="0.85rem" fontWeight="600" textTransform="none" display="inline-flex" alignItems="center"
                  gap={1.5}
                >
                  <Box as="span" fontSize="0.75rem">🔴</Box>
                  Failed
                </Badge>
              );
            }

            return (
              <Badge px={3} py={1} borderRadius="full" bg="#FEF7E0"  color="#B06000" border="1px solid #FDE293"
                fontSize="0.85rem"fontWeight="600" textTransform="none" display="inline-flex" alignItems="center"
                gap={1.5}
              >
                <Spinner size="xs" color="yellow.600" speed="0.8s" />
                🟡 Processing
              </Badge>
            );
          }}
        />

        <Column
          header="Completed Time"
          body={(row: BulkUploadProcessItem) => {
            const isProc = row.isProcess ?? row.IsProcess;
            const isCompleted =
              isProc === 1 || isProc === true || isProc === "1";
            const isFailed =
              isProc === 2 || isProc === "2";

            if (!isCompleted && !isFailed) {
              return <Text color="gray.400">-</Text>;
            }

            const completedTime =
              row.isProcessTime ||
              row.completedTime ||
              row.CompletedTime ||
              row.completedDate ||
              row.updatedOn;

            return <Text>{showDateTime(completedTime)}</Text>;
          }}
        />

        <Column
          header="Failed"
          body={(row: BulkUploadProcessItem) => {
            const failedCount =
              row.failedCount ??
              row.FailedCount ??
              row.failedRows ??
              row.FailedRows ??
              row.failedRecords ??
              row.failed;

            if (failedCount !== undefined && failedCount !== null && Number(failedCount) === 0) {
              return <Text color="gray.400">-</Text>;
            }

            return (
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                borderRadius="md"
                _hover={{ bg: "red.50" }}
                onClick={() => handleViewFailed(row)}
              >
                View Failed {failedCount ? `(${failedCount})` : ""}
              </Button>
            );
          }}
        />

        <Column
        header="Download"
        body={(row: BulkUploadProcessItem) => {
          const processId = row.id ?? row.bulkUploadProcessId ;
          return (
            <Tooltip label="Download Failed Rows">
              <IconButton
                aria-label="Download Failed Rows"
                icon={<DownloadIcon />}
                size="sm"
                colorScheme="blue"
                variant="ghost"
                onClick={() => handleDownloadFailedRows(row)}
                isDisabled={!processId}
              />
            </Tooltip>
          );
        }}
      />

      </CustomTable>

      {/* Failed Records Modal */}
      <Modal
        isOpen={isFailedModalOpen}
        onClose={() => setIsFailedModalOpen(false)}
        size="xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="xl" maxW="850px">
          <ModalHeader borderBottom="1px solid #E2E8F0" pb={3}>
            <Flex direction="column">
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                Failed Records Details
              </Text>
              <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
                File: {selectedProcess?.fileName || selectedProcess?.FileName || "Upload File"}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton mt={1} />

          <ModalBody p={6}>
            {loadingFailedRows ? (
              <Flex justify="center" align="center" py={10} direction="column" gap={3}>
                <Spinner color="red.500" size="lg" />
                <Text fontSize="sm" color="gray.500">
                  Loading failed records...
                </Text>
              </Flex>
            ) : failedRows.length === 0 ? (
              <Flex justify="center" align="center" py={10} direction="column">
                <Text fontSize="md" color="gray.500">
                  No failed records details found for this upload.
                </Text>
              </Flex>
            ) : (
              <TableContainer border="1px solid #E2E8F0" borderRadius="md">
                <Table size="sm" variant="simple">
                  <Thead bg="#F7FAFC">
                    <Tr>
                      <Th width="50px">#</Th>
                      <Th>Contact</Th>
                      <Th>Email</Th>
                      <Th>Phone</Th>
                      <Th>Reason</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {failedRows.map((item, idx) => (
                      <Tr key={idx} _hover={{ bg: "gray.50" }}>
                        <Td fontWeight="500">{idx + 1}</Td>
                        <Td fontWeight="500">{item.contact || item.Contact || "-"}</Td>
                        <Td color="blue.600">{item.email || item.Email || "-"}</Td>
                        <Td>{item.phone || item.Phone || "-"}</Td>
                        <Td>
                          <Badge
                            colorScheme="red"
                            px={2.5}
                            py={1}
                            borderRadius="md"
                            textTransform="none"
                            fontSize="xs"
                            fontWeight="500"
                            whiteSpace="normal"
                          >
                            {item.reason || item.Reason || "Validation Error"}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px solid #E2E8F0">
            <Button variant="ghost" onClick={() => setIsFailedModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BulkUploadLead;
